import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import AnalysisCard from "@/components/base/AnalysisCard";
import { analyses } from "@/mocks/analyses";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Analysis() {
  const [params, setParams] = useSearchParams();
  const articleId = params.get("article") ?? "";
  const selected = analyses.find((a) => a.id === articleId) ?? null;
  const [visible, setVisible] = useState(5);
  const readerRef = useRef<HTMLDivElement>(null);

  usePageMeta(
    "足球赛事解析专题 - 51网站在线观看免费播播足球",
    "51足球直播赛事解析专题，提供战术拆解、赛前前瞻与赛后复盘等深度内容，帮你更专业地看懂每一场比赛。",
  );

  useEffect(() => {
    if (selected && readerRef.current) {
      readerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selected]);

  const closeReader = () => {
    const next = new URLSearchParams(params);
    next.delete("article");
    setParams(next, { replace: true });
  };

  const shown = analyses.slice(0, visible);

  return (
    <SiteLayout>
      <PageHeader
        title="赛事解析专题"
        subtitle="从战术体系到临场调整，用更专业的视角复盘每一场焦点赛事，读懂足球背后的逻辑。"
        crumbs={[{ label: "首页", to: "/" }, { label: "赛事解析" }]}
        aside={
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 text-white text-sm backdrop-blur">
            <i className="ri-article-line text-accent-400" />
            共 {analyses.length} 篇解析
          </div>
        }
      />

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto" ref={readerRef}>
          {selected && (
            <article className="mb-10 bg-background-50 border border-primary-200 rounded-lg overflow-hidden animate-fade-up">
              <div className="relative h-48 md:h-72 w-full overflow-hidden">
                <img
                  src={selected.cover}
                  alt={`${selected.title} 解析配图`}
                  title={`${selected.title} 赛事解析`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {selected.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-md bg-white/20 text-white backdrop-blur"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="text-xl md:text-3xl font-semibold text-white leading-snug">
                    {selected.title}
                  </h2>
                </div>
              </div>
              <div className="p-5 md:p-8">
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400 mb-6 pb-5 border-b border-background-100">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" />
                    {selected.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    {selected.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    阅读约 {selected.readTime}
                  </span>
                  <button
                    type="button"
                    onClick={closeReader}
                    className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line" />
                    收起全文
                  </button>
                </div>
                <div className="max-w-3xl">
                  {selected.content.map((p, i) => (
                    <p
                      key={i}
                      className="text-sm md:text-base text-foreground-700 leading-loose mb-5 last:mb-0"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </article>
          )}

          <div className="flex items-end justify-between mb-5">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight">
              {selected ? "更多解析文章" : "全部解析文章"}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {shown.map((a) => (
              <AnalysisCard key={a.id} analysis={a} />
            ))}
          </div>

          {visible < analyses.length && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + 5)}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-background-50 border border-background-200 hover:bg-primary-50 hover:border-primary-300 text-foreground-800 text-sm font-medium transition-colors cursor-pointer"
              >
                加载更多文章
                <i className="ri-arrow-down-line" />
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}